import {
  AbbrApi,
  ArrowRightFromSquare,
  ArrowsRotateLeft,
  BookOpen,
  ChartAreaStackedNormalized,
  DisplayPulse,
  Ellipsis,
  FolderArrowDown,
  Hand,
  House,
  LayoutList,
  Person,
  QrCode,
} from "@gravity-ui/icons";
import { PageLayout, ActionBar } from "@gravity-ui/navigation";
import { Button, DropdownMenu, Icon, User } from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import { PropsWithChildren } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { POIZON_API_SUPPORT_LINK } from "src/constants/links";
import { RoutesPaths } from "src/routes/RoutesPaths";
import { getMeRequest } from "src/store/fetchers/getMeRequest";
import { logoutRequest } from "src/store/fetchers/logoutRequest";
import { isAdmin } from "src/util";
import { ShowConfirmDialog } from "src/widgets/Alert/Alert";

import { LanguagesBlock } from "../LanguagesBlock";
import { ThemeBlock } from "../ThemeBlock";
import css from "./MenuLayout.module.scss";

const ICON_SIZE = 18;
export const MenuLayoutMobile = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();

  const [isLoading] = useAtom(logoutRequest.isLoadingAtom);
  const logout = useAction(logoutRequest);

  const handleLogout = async () => {
    const result = await ShowConfirmDialog(
      "Вы действительно хотите выйти из аккаунта?",
      "Подтвердите выход",
    );
    if (result) logout();
  };

  const currentPath = useLocation().pathname;

  const [userData] = useAtom(getMeRequest.dataAtom);
  return (
    <div className={css.mobileMarginBottom}>
      <div className={css.webkitFix}>
        <div className={css.bottomNavigation}>
          <Button
            size="xl"
            view="flat"
            onClick={() => navigate(RoutesPaths.LK.PROFILE.BASE_PATH)}
            selected={currentPath.includes(RoutesPaths.LK.PROFILE.BASE_PATH)}
          >
            <Icon data={Person} size={ICON_SIZE} />
          </Button>

          <Button
            size="xl"
            view="flat"
            onClick={() => navigate(RoutesPaths.LK.GOODS)}
            selected={currentPath === RoutesPaths.LK.GOODS}
          >
            <Icon data={LayoutList} size={ICON_SIZE} />
          </Button>
          <Button
            size="xl"
            view="flat"
            onClick={() => navigate(RoutesPaths.LK.SCANNER)}
            selected={currentPath === RoutesPaths.LK.SCANNER}
          >
            <Icon data={QrCode} size={ICON_SIZE} />
          </Button>
          <Button
            size="xl"
            view="flat"
            onClick={() => navigate(RoutesPaths.LK.EXPORT)}
            selected={currentPath === RoutesPaths.LK.EXPORT}
          >
            <Icon data={FolderArrowDown} size={ICON_SIZE} />
          </Button>
          <DropdownMenu
            size="xl"
            renderSwitcher={(props) => (
              <Button
                {...props}
                size="xl"
                view="flat"
                selected={[RoutesPaths.LK.CONVERTER].includes(currentPath)}
              >
                <Icon data={Ellipsis} size={ICON_SIZE} />
              </Button>
            )}
            items={[
              [
                {
                  items: [],
                  text: (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <LanguagesBlock />
                    </div>
                  ),
                },
                {
                  items: [],
                  text: (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <ThemeBlock />
                    </div>
                  ),
                  className: css.themeBlockItem,
                },
                ...(isAdmin(userData)
                  ? [
                      {
                        action: () => navigate(RoutesPaths.ADMIN.BASE_PATH),
                        selected: currentPath.startsWith("/admin"),
                        iconStart: <Icon data={DisplayPulse} />,
                        text: "Админ панель",
                      },
                    ]
                  : []),
                {
                  action: () => navigate(RoutesPaths.LK.SUBSCRIBE),
                  selected: currentPath === RoutesPaths.LK.SUBSCRIBE,
                  iconStart: <Icon data={ChartAreaStackedNormalized} />,
                  text: "Подписки",
                },
                {
                  action: () => navigate(RoutesPaths.LK.CONVERTER),
                  selected: currentPath === RoutesPaths.LK.CONVERTER,
                  iconStart: <Icon data={ArrowsRotateLeft} />,
                  text: "Конвертер ссылок",
                },
                {
                  action: () => navigate(RoutesPaths.LK.API_DOCS),
                  selected: currentPath === RoutesPaths.LK.API_DOCS,
                  iconStart: <Icon data={AbbrApi} />,
                  text: "API документация",
                },
                {
                  action: () => navigate(RoutesPaths.LK.API_KNOWLEDGE_BASE),
                  selected: currentPath === RoutesPaths.LK.API_KNOWLEDGE_BASE,
                  iconStart: <Icon data={BookOpen} />,
                  text: "База знаний",
                },
                {
                  action: () => navigate(RoutesPaths.LANDING),
                  iconStart: <Icon data={House} />,
                  text: "На главную",
                },
              ],
              [
                {
                  action: () => {
                    if (!isLoading) handleLogout();
                  },
                  iconStart: <Icon data={ArrowRightFromSquare} />,
                  text: "Выйти",
                },
              ],
            ]}
          />
        </div>
      </div>
      <PageLayout.Content>
        <ActionBar aria-label="Actions bar" className={css.actionBar}>
          <ActionBar.Section>
            <ActionBar.Group pull="left">
              <ActionBar.Item>
                <Button
                  view="flat-info"
                  href={POIZON_API_SUPPORT_LINK}
                  target="_blank"
                >
                  <Icon data={Hand} />
                  Помощь
                </Button>
              </ActionBar.Item>
            </ActionBar.Group>
            <ActionBar.Group pull="right">
              <ActionBar.Item>
                <div onClick={() => navigate(RoutesPaths.LK.PROFILE.BASE_PATH)}>
                  <User
                    avatar={{
                      text: userData?.name ?? "",
                      theme: "brand",
                    }}
                    name={userData?.name}
                    description={userData?.email}
                    size="xs"
                    className={css.userCustomStyle}
                  />
                </div>
              </ActionBar.Item>
            </ActionBar.Group>
          </ActionBar.Section>
        </ActionBar>
        <div className={css.divPadding}>{children}</div>
      </PageLayout.Content>
    </div>
  );
};
